import type { MessageDescriptor } from "react-intl";
import {
  ListingApplicationState,
  type ListingApplicationEffectiveActionsFragment,
} from "@/graphql/generated/operations";

// Zustand einer Bewerbung und die Wechsel der Eingetragenen
// (@external-adr GENESIS-0087-application-phases-are-separate-from-decisions (GENESIS-0071 §3)).
//
// OB ein Wechsel angeboten wird, entscheidet allein `ListingApplication.actions`: genesis rechnet es
// mit denselben Prüfungen aus wie die Mutation (§4). Diese Tabelle sagt nur, WOHIN ein gemeldeter
// Wechsel führt und wie er heißt; einen Zustandsautomaten kennt der Client nicht.
//
// Angenommen und Abgelehnt sind keine Endzustände: mit WRITE auf `applications` holt "Entscheidung
// zurücknehmen" sie nach In Prüfung zurück. Endgültig ist nur der Rückzug durch die bewerbende Person.

export type ListingApplicationActions = ListingApplicationEffectiveActionsFragment["actions"];

export type ApplicationReview = "startReview" | "accept" | "reject" | "reopenReview";

export interface ApplicationReviewSpec {
  review: ApplicationReview;
  target: ListingApplicationState;
  allowed: (actions: ListingApplicationActions) => boolean;
}

// Reihenfolge = Reihenfolge der Knöpfe: der Schritt nach vorn zuerst, das Zurücknehmen zuletzt.
export const APPLICATION_REVIEWS: readonly ApplicationReviewSpec[] = [
  { review: "startReview", target: ListingApplicationState.InReview, allowed: (a) => a.canStartReview },
  { review: "accept", target: ListingApplicationState.Accepted, allowed: (a) => a.canAccept },
  { review: "reject", target: ListingApplicationState.Rejected, allowed: (a) => a.canReject },
  { review: "reopenReview", target: ListingApplicationState.InReview, allowed: (a) => a.canReopenReview },
];

export function availableReviews(actions: ListingApplicationActions | null | undefined): ApplicationReviewSpec[] {
  if (!actions) return [];
  return APPLICATION_REVIEWS.filter((spec) => spec.allowed(actions));
}

export const LISTING_APPLICATION_STATES: readonly ListingApplicationState[] = [
  ListingApplicationState.Received,
  ListingApplicationState.InReview,
  ListingApplicationState.Accepted,
  ListingApplicationState.Rejected,
  ListingApplicationState.Withdrawn,
];

export const LISTING_APPLICATION_STATE_LABELS: Record<ListingApplicationState, MessageDescriptor> = {
  [ListingApplicationState.Received]: { id: "share.applications.state.received", defaultMessage: "Eingegangen" },
  [ListingApplicationState.InReview]: { id: "share.applications.state.inReview", defaultMessage: "In Prüfung" },
  [ListingApplicationState.Accepted]: { id: "share.applications.state.accepted", defaultMessage: "Angenommen" },
  [ListingApplicationState.Rejected]: { id: "share.applications.state.rejected", defaultMessage: "Abgelehnt" },
  [ListingApplicationState.Withdrawn]: { id: "share.applications.state.withdrawn", defaultMessage: "Zurückgezogen" },
};

export const APPLICATION_REVIEW_LABELS: Record<ApplicationReview, MessageDescriptor> = {
  startReview: { id: "share.listings.applications.action.startReview", defaultMessage: "In Prüfung nehmen" },
  accept: { id: "share.listings.applications.action.accept", defaultMessage: "Annehmen" },
  reject: { id: "share.listings.applications.action.reject", defaultMessage: "Ablehnen" },
  reopenReview: {
    id: "share.listings.applications.action.reopenReview",
    defaultMessage: "Entscheidung zurücknehmen",
  },
};
